import React, { useEffect, useState } from 'react'
import { Icon } from '@iconify/react';
import { ToastContainer } from 'react-toastify';
import ClassroomAssignmentResources from './ClassroomAssignmentResources'
import ClassroomAssignmentResponse from './ClassroomAssignmentResponse'
import { usePermissions } from '../../../context/PermissionsContext';

import { toast } from '../../../components/ui/notify';
import { useParams } from 'react-router-dom';
import { decodeData, encodeData } from '../../../utils/encodeAndEncode';
import ContentUnavailable from '../../../components/common/ContentUnavailable';
import Loader from '../../../components/common/Loader';
import { getClassroomAssignmentDetails } from '../../../services/organization/classroom/classroomAssignment.service';

function ClassroomAssignmentDetails() {
  const [activeTab, setActiveTab] = useState('resources');
  const [contentStatus, setContentStatus] = useState('loading');
  const { permissions } = usePermissions();
  const { classroomId, assignmentId } = useParams();
  const decodedClassroomId = decodeData(classroomId);
  const decodedAssignmentId = decodeData(assignmentId);
  const dashboardPath = decodedClassroomId
    ? `/admin/classrooms/classroom-dashboard/${encodeData(decodedClassroomId)}?active-tab=assignments`
    : '/admin/classrooms';

  useEffect(() => {
    let cancelled = false;

    const verifyAssignment = async () => {
      if (!decodedClassroomId || !decodedAssignmentId) {
        setContentStatus('unavailable');
        return;
      }

      try {
        setContentStatus('loading');
        const response = await getClassroomAssignmentDetails({
          class_id: decodedClassroomId,
          assignment_id: decodedAssignmentId,
        });

        if (cancelled) {
          return;
        }

        if (response?.success && response?.data) {
          setContentStatus('available');
        } else {
          setContentStatus('unavailable');
        }
      } catch (error) {
        if (!cancelled) {
          setContentStatus('unavailable');
        }
      }
    };

    verifyAssignment();

    return () => {
      cancelled = true;
    };
  }, [decodedClassroomId, decodedAssignmentId]);

  const handleTabChange = (tab) => {
    if (tab === 'resources' && !permissions.classroom_assignment_resource_view) {
      toast.warning("You don't have permission to view resources");
      return;
    }
    if (tab === 'response' && !permissions.classroom_assignment_response_view) {
      toast.warning("You don't have permission to view responses");
      return;
    }
    setActiveTab(tab);
  };

  if (contentStatus === 'loading') {
    return <Loader variant="section" caption="Loading assignment..." />;
  }

  if (contentStatus === 'unavailable') {
    return (
      <ContentUnavailable
        title="This assignment is not available"
        description="This assignment may have been removed or deleted from the classroom."
        icon="mdi:file-document-remove-outline"
        backLabel="Back to Classroom"
        backTo={dashboardPath}
      />
    );
  }

  return (
    <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
      <div className="tw-min-w-[0px] ui-column md:tw-w-full">
        <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card"}>
          <div className="max-[768px]:tw-p-[var(--spacing-md)] tw-text-[var(--text-primary)] [flex:1_1_auto] ui-card-body tw-p-0">

            {/* Tabs */}
            <div className="tw-border-solid tw-border-b-[#e0e0e0] tw-bg-[#f8f9fa] tab-header tw-border-b">
              <ul className={"max-[767.98px]:[&[aria-label=\"breadcrumb\"]]:!tw-p-[0.75rem_1rem] max-[767.98px]:[&[aria-label=\"breadcrumb\"]_h4]:tw-text-[1.1rem] tw-flex tw-gap-1 [list-style:none] [&_.ui-nav-item]:tw-m-0 [&_.ui-nav-link:hover]:tw-text-[#3152e8] [&_.ui-nav-link:hover]:tw-border-0 [&_.ui-nav-link.active::after]:[content:''] [&_.ui-nav-link.active::after]:tw-absolute [&_.ui-nav-link.active::after]:tw-bottom-0 [&_.ui-nav-link.active::after]:tw-left-0 [&_.ui-nav-link.active::after]:tw-w-full [&_.ui-nav-link.active::after]:tw-h-[2px] [&_.ui-nav-link.active::after]:tw-bg-[#3152e8] max-[768px]:tw-p-[0_10px] max-[768px]:[&_.ui-nav-link]:tw-p-[12px_15px] max-[768px]:[&_.ui-nav-link]:tw-text-[14px] [&_.ui-nav-link.disabled]:!tw-cursor-not-allowed [&_.ui-nav-link.disabled]:![pointer-events:all] [&_.ui-nav-link.disabled]:tw-opacity-[0.6] [&_button.disabled]:!tw-cursor-not-allowed [&_button.disabled]:![pointer-events:all] [&_button.disabled]:tw-opacity-[0.6] [&_button[disabled]]:!tw-cursor-not-allowed [&_button[disabled]]:![pointer-events:all] [&_button[disabled]]:tw-opacity-[0.6] [&_.ui-nav-link.active]:tw-text-[#3152e8] [&_.ui-nav-link.active]:tw-border-0 [&_.ui-nav-link.active]:tw-rounded-br-[0px] [&_.ui-nav-link.active]:tw-rounded-bl-[0px] [&_.ui-nav-link.active]:tw-border-b-[2px] [&_.ui-nav-link.active]:[--tw-border-opacity:1] [&_.ui-nav-link.active]:tw-border-[rgb(64_84_214_/_var(--tw-border-opacity,_1))] [&_.ui-nav-link.active]:tw-bg-[transparent] tw-border-0 tw-p-[0_20px] tw-mb-5 tw-border-b-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] tw-overflow-x-auto tw-flex-nowrap [scrollbar-width:thin] [&_.ui-nav-link]:tw-border-0 [&_.ui-nav-link]:tw-text-[#6c757d] [&_.ui-nav-link]:tw-p-[15px_25px] [&_.ui-nav-link]:tw-font-medium [&_.ui-nav-link]:[transition:all_0.3s_ease] [&_.ui-nav-link]:tw-relative [&_.ui-nav-link]:tw-whitespace-nowrap ui-nav ui-nav-tabs"}>
                <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                  <button
                    className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "resources" ? "active" : ""}`}
                    onClick={() => handleTabChange('resources')}
                    disabled={!permissions.classroom_assignment_resource_view}
                    title={!permissions.classroom_assignment_resource_view ? "You don't have permission to view resources" : "View assignment resources"}
                  >
                    <Icon icon="fluent:document-24-regular" className="tw-me-2" width="20" height="20" />
                    Resources
                  </button>
                </li>
                <li className={"tw-rounded-[8px] tw-m-[4px_8px] [font-family:var(--font-primary)] [font-weight:var(--font-weight-medium)] tw-text-[#555] [transition:all_0.2s_ease] tw-no-underline tw-relative [&:hover]:tw-bg-[rgba(13,_110,_253,_0.05)] [&:hover]:tw-text-[#0d6efd] [&.active]:tw-bg-[var(--primary-color)] [&.active]:tw-text-[white] [&.tw-text-rose-600]:tw-text-[#dc3545] [&.tw-text-rose-600:hover]:tw-bg-[rgba(220,_53,_69,_0.05)] [&.tw-text-rose-600:hover]:tw-text-[#dc3545] ui-nav-item"}>
                  <button
                    className={`tw-gap-2 tw-rounded-[0.5rem] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:hover]:[--tw-text-opacity:1] [&:hover]:tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [&.active]:[--tw-bg-opacity:1] [&.active]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&.active]:[--tw-text-opacity:1] [&.active]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] ui-nav-link tw-flex tw-items-center   ${activeTab === "response" ? "active" : ""}`}
                    onClick={() => handleTabChange('response')}
                    disabled={!permissions.classroom_assignment_response_view}
                    title={!permissions.classroom_assignment_response_view ? "You don't have permission to view responses" : "View assignment responses"}
                  >
                    <Icon icon="fluent:chat-24-regular" className="tw-me-2" width="20" height="20" />
                    Response
                  </button>
                </li>
              </ul>
            </div>

            {/* Tab Content */}
            <div className={"max-[768px]:tw-p-[2px] tw-bg-[#fff] [&_>_.tab-pane]:tw-hidden [&_>_.tab-pane.active]:tw-block tab-content tw-p-2"}>
              {activeTab === 'resources' && permissions.classroom_assignment_resource_view && (
                <div className="resources">
                  <ClassroomAssignmentResources decodedClassroomId={decodedClassroomId} decodedAssignmentId={decodedAssignmentId} />
                </div>
              )}

              {activeTab === 'response' && permissions.classroom_assignment_response_view && (
                <div className="response">
                  <ClassroomAssignmentResponse decodedClassroomId={decodedClassroomId} decodedAssignmentId={decodedAssignmentId} />
                </div>
              )}

              {((activeTab === 'resources' && !permissions.classroom_assignment_resource_view) ||
                (activeTab === 'response' && !permissions.classroom_assignment_response_view)) && (
                <div className="tw-rounded-[0.75rem] tw-border-[1px] tw-p-4 tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-border-opacity:1] tw-border-[rgb(254_243_199_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_251_235_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(120_53_15_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-warning tw-m-4">
                  You don't have permission to view this content.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer limit={3} />
    </div>
  )
}

export default ClassroomAssignmentDetails
